import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

function Dashboard() {
  const navigate = useNavigate();
  const { user, token, getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && token) {
      fetchDashboardData();
    }
  }, [user, token]);

  const fetchDashboardData = async () => {
    try {
      const headers = getAuthHeader();
      const response = await axios.get(`${API_URL}/dashboard/stats`, headers);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const isApprover = user?.role !== "INDENTER";

  if (loading) {
    return (
      <div className="space-y-8" data-testid="dashboard">
        <div>
          <Skeleton className="h-10 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6"><Skeleton className="h-20" /></Card>
          <Card className="p-6"><Skeleton className="h-20" /></Card>
          <Card className="p-6"><Skeleton className="h-20" /></Card>
          <Card className="p-6"><Skeleton className="h-20" /></Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-500 mt-1">
            Here's what's happening with your procurement activities today.
          </p>
        </div>
        {user?.role === "INDENTER" && (
          <Button 
            onClick={() => navigate("/requisitions/new")}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="new-requisition-btn"
          >
            + New Requisition
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stat-card" data-testid="stat-card-requisitions">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Requisitions</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.requisitions?.total || 0}</p>
                <p className="text-sm text-slate-500 mt-1">{stats?.requisitions?.pending || 0} pending</p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-card-indents">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Indents</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.indents?.total || 0}</p>
                <p className="text-sm text-slate-500 mt-1">{stats?.indents?.pending || 0} pending</p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-purple-50 text-purple-600">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-card-pos">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Purchase Orders</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.purchase_orders?.total || 0}</p>
                <p className="text-sm text-slate-500 mt-1">{stats?.purchase_orders?.active || 0} active</p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-card-payments">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Payments</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.payments?.total || 0}</p>
                <p className="text-sm text-slate-500 mt-1">{stats?.payments?.pending || 0} pending</p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-amber-50 text-amber-600">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-2">
            {user?.role === "INDENTER" ? (
              <>
                <Button variant="outline" className="w-full justify-start h-12" onClick={() => navigate("/requisitions/new")} data-testid="quick-action-requisition">
                  Create Purchase Request
                </Button>
                <Button variant="outline" className="w-full justify-start h-12" onClick={() => navigate("/sanction-proposals/new")} data-testid="quick-action-sanction">
                  Create Sanction Proposal
                </Button>
                <Button variant="outline" className="w-full justify-start h-12" onClick={() => navigate("/payments/new")} data-testid="quick-action-payment">
                  Payment Recommendation
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" className="w-full justify-start h-12" onClick={() => navigate("/approvals")} data-testid="quick-action-approvals">
                  View Approval Queue
                </Button>
                <Button variant="outline" className="w-full justify-start h-12" onClick={() => navigate("/requisitions")} data-testid="quick-action-view-requisitions">
                  View All Purchase Requests
                </Button>
                <Button variant="outline" className="w-full justify-start h-12" onClick={() => navigate("/purchase-orders")} data-testid="quick-action-view-pos">
                  View Purchase Orders
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900">Procurement Rules</CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="font-semibold text-green-900 mb-2">GeM Purchase</h3>
              <p className="text-xs text-green-600 mb-2 italic">Same for General / Research / Scientific</p>
              <ul className="text-sm text-green-700 space-y-1">
                <li>Up to 1L: Direct Purchase (Direct PO)</li>
                <li>1L-10L: OEM Comparison (Direct PO) + Committee(3)</li>
                <li>Above 10L: Tender Module + Committee(3)</li>
                <li>Above 20L: Tender + Director</li>
                <li>Above 25L: Tender + Committee(4) + Director</li>
              </ul>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg">
              <h3 className="font-semibold text-orange-900 mb-2">Non-GeM Purchase</h3>
              <p className="text-xs text-orange-500 font-medium mb-1">a) General / Common Use:</p>
              <ul className="text-sm text-orange-700 space-y-1 mb-3">
                <li>Up to 5L: PAC/LPC (Direct PO) + Committee(3)</li>
                <li>Above 5L: Tender Module (3 / 4 if {">"}25L)</li>
              </ul>
              <p className="text-xs text-orange-500 font-medium mb-1">b) Scientific Equipment:</p>
              <ul className="text-sm text-orange-700 space-y-1">
                <li>Up to 2L: Direct Purchase (Direct PO)</li>
                <li>2L-25L: LPC (Direct PO) + Committee(3), Director if {">"}20L</li>
                <li>Above 25L: Tender Module + Committee(4) + Director</li>
              </ul>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2">Approval Chain</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>S&P: DA → AR → HoS</li>
                <li>F&A: DA → AR → HoS</li>
                <li>Internal Audit + Dean (Admin) for higher values</li>
                <li>Director for above 20L</li>
                <li>Project Funds: Dean (SRC) replaces Dean (Admin)</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Dashboard;
